import { useEffect, useState } from 'react';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { Icon } from '../../../../Atoms/Icon';
import { GridCustomColDef } from '../../../../types';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { DEFAULT_MINI_TABLE_SX_STYLES, DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../../../utils/styleHelpers';
import { updateWizardData, getWizardDataSubject } from '../../../../../store/ProvisionalAsset';

interface DefaultRow {
  field: string;
  label: string;
  mappedField: string;
  labelMappedField: string;
  active?: boolean;
}

const defaultRows: DefaultRow[] = [
  { field: 'supplier', label: 'Supplier', mappedField: 'supplier', labelMappedField: 'RPM Supplier' },
  { field: 'tsd', label: 'TSD', mappedField: 'tsd', labelMappedField: 'TSD' },
  {
    field: 'salesAgent',
    label: 'Sales Agent',
    mappedField: 'assignedAgencyGroup',
    labelMappedField: 'Assigned Agency'
  },
  { field: 'rpmAccountId', label: 'Rpm Account Id', mappedField: 'rpmAccountId', labelMappedField: 'Rpm Account' },
  { field: 'product2id', label: 'Product', mappedField: 'productId', labelMappedField: 'Product' },
  {
    field: 'commissionableMrc',
    label: 'Commissionable MRC',
    mappedField: 'recurringNetBilled',
    labelMappedField: 'Net Billed'
  },
  { field: 'mrc', label: 'Total MRC', mappedField: 'recurringNetBilled', labelMappedField: 'Net Billed' },
  {
    field: 'expectedGrossMrr',
    label: 'Gross MRR',
    mappedField: 'recurringGrossCommission',
    labelMappedField: 'Gross Commission'
  },
  { field: 'commissionableNrc', label: 'Commissionable NRC', mappedField: 'netBilled', labelMappedField: 'Net Billed' },
  {
    field: 'expectedGrossNrr',
    label: 'Gross NRR',
    mappedField: 'grossCommissionExpected',
    labelMappedField: 'Gross Commission'
  },
  { field: 'circuitId', label: 'Circuit ID', mappedField: 'circuitId', labelMappedField: 'Circuit ID' },
  { field: 'location', label: 'Location', mappedField: 'address', labelMappedField: 'Location' },
  { field: 'cancelledDate', label: 'Cancelled Date', mappedField: 'cancelDate', labelMappedField: 'Cancelled Date' },
  {
    field: 'disconnectDate',
    label: 'Disconnect Date',
    mappedField: 'disconnectDate',
    labelMappedField: 'Disconnect Date'
  },
  {
    field: 'tsdTrackingNumber',
    label: 'TSD Tracking Number',
    mappedField: 'tsdOrderId',
    labelMappedField: 'TSD Order Id'
  },
  {
    field: 'nonCommissionableNrc',
    label: 'Non Commissionable NRC',
    mappedField: '',
    labelMappedField: 'Non Commissionable NRC'
  },
  {
    field: 'commissionStatus',
    label: 'Commission Status',
    mappedField: '',
    labelMappedField: 'Commission Status'
  },
  {
    field: 'provisionalType',
    label: 'Provisional Type',
    mappedField: '',
    labelMappedField: 'Provisional Type'
  },
  {
    field: 'data.contractStartDate',
    label: 'Contract Sign Date',
    mappedField: 'contractStartDate',
    labelMappedField: 'Contract Start Date'
  },
  { field: 'data.contractTerm', label: 'Term', mappedField: 'contractTerm', labelMappedField: 'Term' }
];

interface RPMFieldMappingStepProps {
  nextStep: () => void;
  title: string;
}

export const RPMFieldMappingStep = ({ nextStep, title }: RPMFieldMappingStepProps) => {
  const [mapFields, setMapFields] = useState<DefaultRow[]>([]);
  const [isValidStep, setIsValidStep] = useState<boolean>(false);

  useEffect(() => {
    const rows = defaultRows.map((row, index) => ({ id: index, active: true, ...row }));
    setIsValidStep(rows.some((r) => r.active));
    setMapFields(rows);
  }, []);

  const handleRowClick = (row: DefaultRow) => {
    const updatedRows = mapFields.map((r) => {
      if (r.field === row.field) {
        return { ...r, active: !r.active };
      }
      return r;
    });
    setIsValidStep(updatedRows.some((r) => r.active));
    setMapFields(updatedRows);
  };

  const columns: GridCustomColDef[] = [
    { field: 'labelMappedField', headerName: 'Source Field', flex: 1, minWidth: 150, filterable: false },
    {
      field: 'empty',
      headerName: '',
      maxWidth: 30,
      filterable: false,
      sortable: false,
      renderCell: () => {
        return (
          <div className="inline-flex justify-center w-full">
            <div className="dropdown-triangle p-1" />
          </div>
        );
      }
    },
    { field: 'label', headerName: 'Provisional Asset Field', flex: 1, minWidth: 150, filterable: false },
    {
      field: 'active',
      headerName: '',
      maxWidth: 30,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const type = params.row.active ? 'trash' : 'plus';
        return (
          <div className="inline-flex justify-center w-full">
            <Icon
              type={type}
              onClick={() => handleRowClick(params.row)}
              className="cursor-pointer"
            />
          </div>
        );
      }
    }
  ];

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
      marginLeft: 'auto'
    },
    '.MuiDataGrid-cell': {
      ...DEFAULT_MINI_TABLE_SX_STYLES['.MuiDataGrid-cell'],
      fontSize: '12px'
    }
  };

  const submitStep = () => {
    if (!isValidStep) return;

    const assetMapping: Record<string, string> = mapFields
      .filter((r) => r.active)
      .reduce(
        (acc, row) => {
          acc[row.field] = row.mappedField;
          return acc;
        },
        {} as Record<string, string>
      );
    assetMapping['accountId'] = 'sfdcCustomerId';
    const wizardData = getWizardDataSubject().getValue();
    updateWizardData({ ...wizardData, assetMapping });

    nextStep();
  };

  const cardActions = () => (
    <button
      className={DEFAULT_ADVANCE_BUTTON_STYLE}
      onClick={submitStep}
      data-cy="next-step-button"
      disabled={!isValidStep}
      data-testid="next-step-button">
      Confirm Mapping
    </button>
  );

  return (
    <PageCard
      dataTestId="rpm-field-mapping-step"
      dataCy="rpm-field-mapping-step"
      childrenWrapperClass="max-w-[60vw] min-w-full"
      title={title}
      cardActions={cardActions()}>
      <BasicDataGrid
        rows={mapFields}
        columns={columns}
        sxStyle={sxStyle}
        disableColumnMenu
        disableColumnResize
        showColumnVerticalBorder
        showCellVerticalBorder
      />
    </PageCard>
  );
};
